<template>
    <main-layout>
        <inertia-head>
            <title>{{ vehicleName }}</title>
        </inertia-head>

        <template #header>
            <h2 class="font-bold text-xl text-white leading-tight">
                {{ divisionName }}
            </h2>
        </template>

        <!-- Photos -->
        <div class="bg-white shadow mx-auto">
            <!-- For Large Screen -->
            <div class="hidden lg:block grid grid-cols-2">
                <div class="grid grid-cols-2">
                    <div>
                        <img
                            class="cursor-pointer"
                            :src="mainPhoto"
                            @click="showPhotoZoomer(photos)"
                        />
                    </div>
                    <div>
                        <photos-component
                            :photos="photos"
                            @zoom="showPhotoZoomer"
                        ></photos-component>
                    </div>
                </div>
            </div>

            <!-- For Small Screen -->
            <responsive-photos-component
                class="lg:hidden"
                :photos="photos"
            ></responsive-photos-component>
        </div>

        <!-- Vehicle Header -->
        <div class="bg-white shadow mx-auto">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div
                    class="px-4 lg:px-16 py-4 bg-white border-b border-gray-200"
                >
                    <div class="flex">
                        <div class="flex-grow">
                            <div
                                class="
                                    my-4
                                    text-3xl
                                    lg:text-4xl
                                    font-semibold
                                    text-secondary
                                    tracking-wide
                                "
                            >
                                {{ vehicleName }}
                            </div>
                            <div
                                class="
                                    py-4
                                    text-2xl
                                    lg:text-3xl
                                    text-secondary
                                    tracking-wide
                                "
                            >
                                {{ vehicle.trim }}
                            </div>
                            <div class="my-4 flex">
                                <div
                                    class="pr-4 border-r-2"
                                    :class="detailsClasses"
                                >
                                    <span v-if="vehicle.miles">
                                        {{ formatNumber(vehicle.miles) }} mi
                                    </span>
                                    <span v-else>No mileage data</span>
                                </div>
                                <div
                                    class="px-4 border-r-2"
                                    :class="detailsClasses"
                                    v-if="vehicle.engine"
                                >
                                    {{ vehicle.engine }}
                                </div>
                                <div
                                    class="px-4 border-r-2"
                                    :class="detailsClasses"
                                    v-if="vehicle.color"
                                >
                                    {{ vehicle.color }}
                                </div>
                                <div
                                    class="pl-4"
                                    :class="detailsClasses"
                                    v-if="vehicle.driveline"
                                >
                                    {{ vehicle.driveline }}
                                </div>
                            </div>
                            <div
                                class="my-4 text-gray-300 italic"
                                v-if="isStockPhoto"
                            >
                                There are stock photos only.
                            </div>
                            <div
                                class="
                                    lg:hidden
                                    mt-8
                                    mb-4
                                    text-secondary
                                    font-medium
                                    text-3xl
                                "
                                v-if="vehicle.usd_reserve"
                            >
                                ${{ formatNumber(vehicle.usd_reserve) }}
                            </div>
                            <div
                                class="
                                    lg:hidden
                                    mt-8
                                    mb-4
                                    text-gray-300
                                    italic
                                    text-lg
                                "
                                v-else
                            >
                                Reserve Is Not Set Yet
                            </div>

                            <!-- Buttons For Small Screen -->
                            <div
                                class="
                                    lg:hidden
                                    grid grid-cols-1
                                    justify-items-center
                                    mt-8
                                    mb-3
                                "
                            >
                                <!-- Buy Now Button -->
                                <div
                                    class="
                                        font-medium
                                        text-white
                                        bg-primary
                                        text-center
                                        w-72
                                        py-2.5
                                        px-2
                                        mb-4
                                        rounded-3xl
                                        tracking-wider
                                    "
                                    v-if="vehicle.usd_reserve"
                                    @click="showOffer(true)"
                                >
                                    Buy Now
                                </div>

                                <!-- Make An Offer Button -->
                                <div
                                    class="
                                        font-medium
                                        text-primary
                                        border-2 border-primary
                                        text-center
                                        w-72
                                        py-2.5
                                        px-2
                                        mb-4
                                        rounded-3xl
                                        tracking-wider
                                    "
                                    @click="showOffer(false)"
                                >
                                    Make An Offer
                                </div>

                                <!-- Ask A Question Button -->
                                <div
                                    class="
                                        font-medium
                                        text-primary
                                        border-2 border-primary
                                        text-center
                                        w-72
                                        py-2.5
                                        px-2
                                        mb-4
                                        rounded-3xl
                                        tracking-wider
                                    "
                                    @click="showInfo"
                                >
                                    Ask A Question?
                                </div>

                                <!-- View Vehicle Inspections -->
                                <div
                                    class="
                                        mb-4
                                        text-center text-primary
                                        font-medium
                                        tracking-wide
                                    "
                                    @click="scrollToInspections"
                                >
                                    <i class="icon-np-chevron-double-down"></i>
                                    View Vehicle Inspections
                                </div>
                            </div>
                        </div>
                        <div class="hidden lg:block flex-shrink">
                            <!-- Reserve -->
                            <div
                                class="
                                    mt-4
                                    mb-8
                                    text-secondary
                                    font-medium
                                    text-4xl text-right
                                "
                                v-if="vehicle.usd_reserve"
                                title="Reserve"
                            >
                                ${{ formatNumber(vehicle.usd_reserve) }}
                            </div>
                            <div
                                class="
                                    mt-4
                                    mb-8
                                    text-gray-300
                                    italic
                                    text-lg text-right
                                "
                                v-else
                                title="Reserve"
                            >
                                Reserve Is Not Set Yet
                            </div>

                            <!-- Buy Now Button -->
                            <div
                                class="
                                    hidden
                                    lg:block
                                    font-medium
                                    text-white
                                    bg-primary
                                    text-center
                                    w-72
                                    py-2.5
                                    px-2
                                    mb-4
                                    cursor-pointer
                                    rounded-3xl
                                    tracking-wider
                                    hover:bg-secondary
                                    transition-all
                                    duration-300
                                "
                                v-if="vehicle.usd_reserve"
                                @click="showOffer(true)"
                            >
                                Buy Now
                            </div>

                            <!-- Make An Offer Button -->
                            <div
                                class="
                                    hidden
                                    lg:block
                                    font-medium
                                    text-primary
                                    border-2 border-primary
                                    text-center
                                    w-72
                                    py-2.5
                                    px-2
                                    mb-4
                                    cursor-pointer
                                    rounded-3xl
                                    tracking-wider
                                    hover:bg-secondary
                                    hover:text-white
                                    hover:border-secondary
                                    transition-all
                                    duration-300
                                "
                                @click="showOffer(false)"
                            >
                                Make An Offer
                            </div>

                            <!-- Ask A Question Button -->
                            <div
                                class="
                                    hidden
                                    lg:block
                                    font-medium
                                    text-primary
                                    border-2 border-primary
                                    text-center
                                    w-72
                                    py-2.5
                                    px-2
                                    mb-4
                                    cursor-pointer
                                    rounded-3xl
                                    tracking-wider
                                    hover:bg-secondary
                                    hover:text-white
                                    hover:border-secondary
                                    transition-all
                                    duration-300
                                "
                                @click="showInfo"
                            >
                                Ask A Question?
                            </div>

                            <!-- View Vehicle Inspections -->
                            <div
                                class="
                                    hidden
                                    lg:block
                                    mb-4
                                    text-center text-primary
                                    font-medium
                                    tracking-wide
                                    hover:text-secondary
                                    transition-all
                                    duration-300
                                    cursor-pointer
                                "
                                @click="scrollToInspections"
                            >
                                <i class="icon-np-chevron-double-down"></i>
                                View Vehicle Inspections
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Division Rep -->
        <div class="mx-auto" v-if="rep">
            <div class="bg-secondary">
                <div class="max-w-7xl mx-auto px-4 lg:px-8">
                    <!-- For Large Screen -->
                    <div
                        class="
                            hidden
                            lg:flex lg:px-16
                            py-8
                            text-white
                            tracking-wider
                            justify-center
                        "
                    >
                        <span class="font-light mr-6">
                            Any questions? Contact us!
                        </span>
                        <span class="mx-2">
                            <i class="icon-np-support"></i>
                        </span>
                        <span class="font-semibold mx-2">
                            {{ rep.firstname }} {{ rep.lastname }}
                        </span>
                        <span class="mx-2">•</span>
                        <span class="underline mx-2" title="Text or call us!">
                            <a :href="'tel:+1-' + formatPhone(rep.phone)">
                                {{ formatPhone(rep.phone) }}
                            </a>
                        </span>
                        <span class="mx-2">•</span>
                        <span class="underline mx-2" title="Send us">
                            <span v-html="rep.emailLink"></span>
                        </span>
                    </div>

                    <!-- For Small Screen -->
                    <div
                        class="
                            lg:hidden
                            px-16
                            py-8
                            text-white
                            tracking-wider
                            flex flex-wrap
                            justify-center
                        "
                    >
                        <div class="font-light w-full flex justify-center">
                            Any questions? Contact us!
                        </div>
                        <div class="w-full flex justify-center">
                            <span class="mx-2">
                                <i class="icon-np-support"></i>
                            </span>
                            <span class="font-semibold mx-2">
                                {{ rep.firstname }} {{ rep.lastname }}
                            </span>
                        </div>
                        <div
                            class="w-full flex justify-center underline"
                            title="Text or cell us!"
                        >
                            <a :href="'tel:+1-' + formatPhone(rep.phone)">
                                {{ formatPhone(rep.phone) }}
                            </a>
                        </div>
                        <div
                            class="w-full flex justify-center underline"
                            title="Send us"
                        >
                            <span v-html="rep.emailLink"></span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="flex justify-center">
                <div class="arrow-down"></div>
            </div>
        </div>

        <!-- Vehicle Details -->
        <div class="py-12">
            <div class="max-w-7xl mx-auto px-4 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm">
                    <div
                        class="
                            px-4
                            py-8
                            lg:px-16 lg:py-12
                            bg-white
                            border-b border-gray-200
                        "
                    >
                        <!-- For Large Screen -->
                        <div class="hidden lg:flex gap-8">
                            <!-- Main Photo -->
                            <div class="flex-shrink">
                                <img
                                    v-if="photos && photos.length > 0"
                                    :src="mainPhoto"
                                    class="rounded-lg max-w-md"
                                />
                            </div>

                            <!-- Vehicle Information -->
                            <div class="flex-grow">
                                <div
                                    class="
                                        block
                                        text-3xl
                                        font-medium
                                        text-secondary
                                        tracking-wide
                                    "
                                >
                                    {{ vehicleName }}
                                </div>
                                <div
                                    class="
                                        block
                                        text-lg text-secondary
                                        tracking-wide
                                        mb-8
                                    "
                                >
                                    {{ vehicle.trim }}
                                </div>

                                <div
                                    class="
                                        block
                                        text-secondary
                                        tracking-wide
                                        mb-4
                                    "
                                >
                                    <!-- Stock No. -->
                                    <span v-if="vehicle.stock_no">
                                        <span class="font-medium mr-2">
                                            Stock No:
                                        </span>
                                        {{ vehicle.stock_no }}<br />
                                    </span>

                                    <!-- Mileage -->
                                    <span v-if="vehicle.miles">
                                        <span class="font-medium mr-2">
                                            Mileage:
                                        </span>
                                        <span v-if="vehicle.miles">
                                            {{ formatNumber(vehicle.miles) }} mi
                                        </span>
                                        <span v-else>No mileage data</span>
                                        <br />
                                    </span>

                                    <!-- Engine -->
                                    <span v-if="vehicle.engine">
                                        <span class="font-medium mr-2">
                                            Engine:
                                        </span>
                                        {{ vehicle.engine }}<br />
                                    </span>

                                    <!-- Exterior Color -->
                                    <span v-if="vehicle.color">
                                        <span class="font-medium mr-2">
                                            Exterior Color:
                                        </span>
                                        {{ vehicle.color }}<br />
                                    </span>

                                    <!-- Driveline -->
                                    <span v-if="vehicle.driveline">
                                        <span class="font-medium mr-2">
                                            Driveline:
                                        </span>
                                        {{ vehicle.driveline }}<br />
                                    </span>

                                    <!-- Transmission -->
                                    <span v-if="vehicle.transmission">
                                        <span class="font-medium mr-2">
                                            Transmission:
                                        </span>
                                        {{ vehicle.transmission }}<br />
                                    </span>

                                    <!-- VIN -->
                                    <span v-if="vehicle.vin">
                                        <span class="font-medium mr-2">
                                            VIN:
                                        </span>
                                        <inertia-link
                                            :href="route('contact')"
                                            class="
                                                cursor-pointer
                                                text-primary
                                                hover:text-primary-light
                                            "
                                            title="Contact us for full VIN"
                                        >
                                            {{ vehicle.vin }}
                                        </inertia-link>
                                    </span>
                                </div>
                            </div>

                            <!-- Reserve -->
                            <div
                                class="
                                    flex-shrink
                                    text-secondary
                                    font-medium
                                    text-3xl
                                    float-right
                                    tracking-wide
                                "
                                v-if="vehicle.usd_reserve"
                                title="Reserve"
                            >
                                ${{ formatNumber(vehicle.usd_reserve) }}
                            </div>
                        </div>

                        <!-- For Small Screen -->
                        <div class="lg:hidden">
                            <!-- Main Photo -->
                            <img
                                v-if="photos && photos.length > 0"
                                :src="mainPhoto"
                                class="rounded-lg"
                            />

                            <!-- Vehicle Information -->
                            <div>
                                <div
                                    class="
                                        block
                                        mt-4
                                        mb-2
                                        text-2xl
                                        font-medium
                                        text-secondary
                                        tracking-wide
                                    "
                                >
                                    {{ vehicleName }}
                                </div>
                                <div
                                    class="
                                        block
                                        text-lg text-secondary
                                        tracking-wide
                                        mb-4
                                    "
                                >
                                    {{ vehicle.trim }}
                                </div>

                                <!-- Reserve -->
                                <div
                                    class="
                                        mb-4
                                        text-secondary
                                        font-medium
                                        text-2xl
                                        tracking-wide
                                    "
                                    v-if="vehicle.usd_reserve"
                                    title="Reserve"
                                >
                                    ${{ formatNumber(vehicle.usd_reserve) }}
                                </div>

                                <div
                                    class="
                                        block
                                        text-secondary
                                        tracking-wide
                                        mb-4
                                    "
                                >
                                    <!-- Stock No. -->
                                    <span v-if="vehicle.stock_no">
                                        <span class="font-medium mr-2">
                                            Stock No:
                                        </span>
                                        {{ vehicle.stock_no }}<br />
                                    </span>

                                    <!-- Mileage -->
                                    <span v-if="vehicle.miles">
                                        <span class="font-medium mr-2">
                                            Mileage:
                                        </span>
                                        <span v-if="vehicle.miles">
                                            {{ formatNumber(vehicle.miles) }} mi
                                        </span>
                                        <span v-else>No mileage data</span>
                                        <br />
                                    </span>

                                    <!-- Engine -->
                                    <span v-if="vehicle.engine">
                                        <span class="font-medium mr-2">
                                            Engine:
                                        </span>
                                        {{ vehicle.engine }}<br />
                                    </span>

                                    <!-- Exterior Color -->
                                    <span v-if="vehicle.color">
                                        <span class="font-medium mr-2">
                                            Exterior Color:
                                        </span>
                                        {{ vehicle.color }}<br />
                                    </span>

                                    <!-- Driveline -->
                                    <span v-if="vehicle.driveline">
                                        <span class="font-medium mr-2">
                                            Driveline:
                                        </span>
                                        {{ vehicle.driveline }}<br />
                                    </span>

                                    <!-- Transmission -->
                                    <span v-if="vehicle.transmission">
                                        <span class="font-medium mr-2">
                                            Transmission:
                                        </span>
                                        {{ vehicle.transmission }}<br />
                                    </span>

                                    <!-- VIN -->
                                    <span v-if="vehicle.vin">
                                        <span class="font-medium mr-2"
                                            >VIN:
                                        </span>
                                        <inertia-link
                                            :href="route('contact')"
                                            class="text-primary"
                                            title="Contact us for full VIN"
                                        >
                                            {{ vehicle.vin }}
                                        </inertia-link>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Tire Condition -->
        <damage-component
            id="tire_condition"
            v-if="tireData && tireData.length > 0"
            title="Tire Condition"
            :headers="tireConditionHeaders"
            :damages="tireData"
            @zoom="showPhotoZoomer"
        ></damage-component>

        <!-- Tire Damages -->
        <damage-component
            v-if="tireDamages && tireDamages.length > 0"
            title="Tire Damages"
            :damages="tireDamages"
            @zoom="showPhotoZoomer"
        ></damage-component>

        <!-- Dent Damages -->
        <damage-component
            v-if="dents && dents.length > 0"
            title="Dent Damages"
            :damages="dents"
            @zoom="showPhotoZoomer"
        ></damage-component>

        <!-- Burn Damages -->
        <damage-component
            v-if="burns && burns.length > 0"
            title="Burn Damages"
            :damages="burns"
            @zoom="showPhotoZoomer"
        ></damage-component>

        <!-- Other Damages -->
        <damage-component
            v-if="damages && damages.length > 0"
            title="Other Damages"
            :damages="damages"
            @zoom="showPhotoZoomer"
        ></damage-component>

        <!-- Photo Zoomer Modal -->
        <photo-zoomer-component
            v-model="showPhotoZoomerModal"
            :photos="photosZoom"
            :selectedPhotoIndex="selectedPhoto"
            @cancel="closePhotoZoomer"
        ></photo-zoomer-component>

        <!-- Info Modal -->
        <info-modal-component
            v-model="showInfoModal"
            :vehicle="vehicle"
            @cancel="closeInfo"
            class="hidden lg:block"
        ></info-modal-component>

        <!-- Responsive Info Modal -->
        <responsive-info-modal-component
            v-model="showInfoModal"
            :vehicle="vehicle"
            @cancel="closeInfo"
            class="lg:hidden"
        ></responsive-info-modal-component>

        <!-- Offer Modal -->
        <offer-modal-component
            v-model="showOfferModal"
            :vehicle="vehicle"
            :isBuyNow="isBuyNow"
            @cancel="closeOffer"
            class="hidden lg:block"
        ></offer-modal-component>

        <!-- Responsive Offer Modal -->
        <responsive-offer-modal-component
            v-model="showOfferModal"
            :vehicle="vehicle"
            :isBuyNow="isBuyNow"
            @cancel="closeOffer"
            class="lg:hidden"
        ></responsive-offer-modal-component>
    </main-layout>
</template>

<style scoped>
.arrow-down {
    width: 0;
    height: 0;
    border-left: 3rem solid transparent;
    border-right: 3rem solid transparent;
    border-top: 2rem solid #333333;
}
</style>

<script>
import MainLayout from "@/Layouts/Main";
import PhotosComponent from "@/Components/Vehicle/Photos";
import DamageComponent from "@/Components/Vehicle/Damage";
import PhotoZoomerComponent from "@/Components/Vehicle/Photo-Zoomer";
import InfoModalComponent from "@/Components/Vehicle/Info-Modal";
import OfferModalComponent from "@/Components/Vehicle/Offer-Modal";
import ResponsivePhotosComponent from "@/Components/Vehicle/Responsive-Photos";
import ResponsiveInfoModalComponent from "@/Components/Vehicle/Responsive-Info-Modal";
import ResponsiveOfferModalComponent from "@/Components/Vehicle/Responsive-Offer-Modal";
import { capitalize } from "lodash";

export default {
    components: {
        MainLayout,
        PhotosComponent,
        DamageComponent,
        PhotoZoomerComponent,
        InfoModalComponent,
        OfferModalComponent,
        ResponsivePhotosComponent,
        ResponsiveInfoModalComponent,
        ResponsiveOfferModalComponent,
    },
    props: {
        vehicle: {
            type: Object,
            required: true,
        },
        rep: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            detailsClasses: ["text-gray-500", "tracking-wide"],
            tireConditionHeaders: {
                location: "Tire",
                maker: "Tire Maker",
                description: "Description",
            },
            photosZoom: [],
            selectedPhoto: 0,
            isBuyNow: false,
            showPhotoZoomerModal: false,
            showInfoModal: false,
            showOfferModal: false,
        };
    },
    computed: {
        vehicleName() {
            return (
                this.vehicle.year +
                " " +
                this.vehicle.make +
                " " +
                this.vehicle.model
            );
        },
        divisionName() {
            return capitalize(this.vehicle.selling_location) + " Division";
        },
        photos() {
            return this.vehicle.photos.photos;
        },
        mainPhoto() {
            return this.photos && this.photos.length > 0
                ? this.photos[0]
                : null;
        },
        isStockPhoto() {
            return this.vehicle.photos.is_stock_photo;
        },
        tireData() {
            return this.vehicle.tire_data;
        },
        tireDamages() {
            return this.vehicle.tire_damages;
        },
        dents() {
            return this.vehicle.damages ? this.vehicle.damages.dents : null;
        },
        burns() {
            return this.vehicle.damages ? this.vehicle.damages.burns : null;
        },
        damages() {
            return this.vehicle.damages ? this.vehicle.damages.other : null;
        },
    },
    methods: {
        formatNumber(value) {
            let val = Math.round(value);
            return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        },
        formatPhone(value) {
            let result = value.toString();
            if (result.length === 10) {
                result = result.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
            }
            return result;
        },
        showPhotoZoomer(photos, index) {
            this.photosZoom = photos;
            this.selectedPhoto = index ? index : 0;
            this.showPhotoZoomerModal = true;
        },
        closePhotoZoomer() {
            this.photosZoom = [];
            this.showPhotoZoomerModal = false;
        },
        showInfo() {
            this.showInfoModal = true;
        },
        closeInfo() {
            this.showInfoModal = false;
        },
        showOffer(isBuyNow) {
            this.isBuyNow = isBuyNow;
            this.showOfferModal = true;
        },
        closeOffer() {
            this.showOfferModal = false;
        },
        scrollToInspections() {
            document.getElementById("tire_condition").scrollIntoView({
                behavior: "smooth",
            });
        },
    },
};
</script>
